/**
 * Auth hook — login, logout, session restore.
 *
 * Uses direct fetch for login/refresh/logout (not apiClient, since
 * these endpoints don't need an existing access token).
 */

import { useCallback } from 'react'
import { useAuthStore, type AuthUser } from '@/stores/authStore'
import { usePermissionStore } from '@/stores/permissionStore'
import { errorDetailText } from '@/utils/errorDetail'

interface UseAuthReturn {
  /** Log in with email and password. Throws on failure. */
  login: (email: string, password: string) => Promise<AuthUser>
  /** Log out: revoke refresh token, clear state. */
  logout: () => Promise<void>
  /** Attempt to restore a session from the httpOnly refresh cookie. */
  tryRefresh: () => Promise<boolean>
}

/** How long logout waits for the admin refresh before using the kept token. */
export const LOGOUT_REFRESH_TIMEOUT_MS = 3000

/** A fresh admin access token, or `fallback` on any failure or timeout. */
async function freshAdminToken(fallback: string): Promise<string> {
  let timer: ReturnType<typeof setTimeout> | undefined
  // When the bound wins, abort the refresh BEFORE logout's POST goes out: a
  // slow refresh that completed afterwards would rotate the cookie the logout
  // revoked and could set a valid admin cookie after logout cleared it.
  const controller = new AbortController()
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      controller.abort()
      resolve(null)
    }, LOGOUT_REFRESH_TIMEOUT_MS)
  })
  const refresh = (async (): Promise<string | null> => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        signal: controller.signal,
      })
      if (!response.ok) return null
      const data = await response.json()
      return typeof data?.access_token === 'string' && data.access_token ? data.access_token : null
    } catch {
      return null
    }
  })()
  try {
    return (await Promise.race([refresh, timeout])) ?? fallback
  } finally {
    clearTimeout(timer)
  }
}

export function useAuth(): UseAuthReturn {
  const { setAuth, clearAuth, setLoading, setInitialized } = useAuthStore()

  const login = useCallback(async (email: string, password: string): Promise<AuthUser> => {
    setLoading(true)
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })

      if (!response.ok) {
        const body = await response.json().catch(() => null)
        throw new Error(errorDetailText(body, 'Login failed'))
      }

      const data = await response.json()
      const user: AuthUser = {
        ...data.user,
        roles: data.user.roles || [],
      }
      setAuth(user, data.access_token, {
        mustChangePassword: !!data.must_change_password,
        mustAcceptDisclaimer: !!data.must_accept_disclaimer,
      })

      // Load RBAC permissions after successful login
      usePermissionStore.getState().loadPermissions()

      return user
    } catch (err) {
      setLoading(false)
      throw err
    }
  }, [setAuth, setLoading])

  const logout = useCallback(async () => {
    // SA-922: a logout ends a view-as too, so the server gets its end line.
    // Sent as the ADMIN with a raw fetch, because apiClient would send the
    // target's token and stopImpersonation would hand the end to the banner.
    // The kept admin token is an ordinary 15-minute access token and nothing
    // refreshes it during a view-as (up to 120 minutes), so first refresh as
    // the admin (the cookie is the admin's), bounded so a refresh that never
    // answers cannot hold logout hostage. The refresh must finish before the
    // logout POST: it rotates the cookie and the POST revokes the current one.
    // The DELETE itself is never awaited (best effort, keepalive).
    const { impersonation, user } = useAuthStore.getState()
    if (impersonation && user) {
      const token = await freshAdminToken(impersonation.actorToken)
      fetch(`/api/admin/impersonation/${encodeURIComponent(user.id)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
        keepalive: true,
      }).catch(() => {
        // Best effort, as on Stop (SA-906 spec §3.3).
      })
    }
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      })
    } catch {
      // Best-effort — clear local state regardless
    }
    // The user moves to null: the identity-change subscription
    // (services/identityChange.ts) stops the settings sync and clears
    // React Query, permissions and every per-user store.
    clearAuth()
  }, [clearAuth])

  const tryRefresh = useCallback(async (): Promise<boolean> => {
    setLoading(true)
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      })

      if (response.ok) {
        const data = await response.json()

        // Fetch user profile with the new access token
        const meResponse = await fetch('/api/auth/me', {
          headers: { Authorization: `Bearer ${data.access_token}` },
        })

        if (meResponse.ok) {
          const user = await meResponse.json()
          const authUser: AuthUser = {
            id: user.id,
            email: user.email,
            display_name: user.display_name,
            role: user.role,
            roles: user.roles || [],
          }
          setAuth(authUser, data.access_token, {
            mustChangePassword: !!user.must_change_password,
            mustAcceptDisclaimer: !!user.must_accept_disclaimer,
          })
          setInitialized()

          // Load RBAC permissions after session restore
          usePermissionStore.getState().loadPermissions()

          return true
        }
      }
    } catch {
      // Refresh failed — no existing session
    }

    setLoading(false)
    setInitialized()
    return false
  }, [setAuth, setLoading, setInitialized])

  return { login, logout, tryRefresh }
}
